import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Scan, Clipboard, CheckCircle2, Crown } from "lucide-react";

const WorkflowPage = () => {
  const processFeatures = [
    {
      icon: <Scan className="w-8 h-8 text-black-600" />,
      title: "Precise",
      description:
        "Advanced digital scanning technology ensures accurate impressions every time.",
    },
    {
      icon: <Clipboard className="w-8 h-8 text-black-600" />,
      title: "Smooth Experience",
      description:
        "No traditional impressions needed – completely digital workflow.",
    },
    {
      icon: <CheckCircle2 className="w-8 h-8 text-black-600" />,
      title: "Predictable",
      description:
        "Consistent, reliable results with a proven methodology.",
    },
  ];

  const stages = [
    {
      id: "Stage 0",
      badge: "0",
      title: "Pre-op Scans & Photos",
      subtitle: "Foundation Setup",
      bg: "bg-blue-50",
      description:
        "Take required pre-op records to set a precise foundation for your full arch case.",
      icon: <Scan className="w-7 h-7" />,
    },
    {
      id: "Stage 1",
      badge: "1",
      title: "Surgery Day Design",
      subtitle: "Guided Procedure",
       bg: "bg-green-50",
      description:
        "We are available for guidance throughout the procedure to support your surgical day workflow.",
      icon: <Clipboard className="w-7 h-7" />,
    },
    {
      id: "Stage 2",
      badge: "2",
      title: "Design Adjustments + New Records",
      subtitle: "Refinement",
        bg: "bg-yellow-50",
      description:
        "We’ll adjust the design to meet both the patient’s expectations and the clinic’s standards.",
      icon: <CheckCircle2 className="w-7 h-7" />,
    },
    {
      id: "Stage Final",
      badge: "✔",
      title: "Delivery of Final Prosthesis",
      subtitle: "Completed Case",
          bg: "bg-purple-50",
      description:
        "We deliver a high-quality final prosthesis built for durability, comfort, and aesthetics.",
      icon: <Crown className="w-7 h-7" />,
    },
  ];

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h6 className="text-white font-medium mb-4 tracking-wide uppercase">
            Precise. Smooth Experience. Consistent.
          </h6>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-8">
            Full Arch Zirconia in{" "}
            <span className="from-black-400 to-cyan-300 bg-clip-text">
              Just 3 Appointments
            </span>
          </h1>
          <p className="text-lg text-white max-w-3xl mx-auto leading-relaxed mb-10">
           A streamlined workflow for stunning, long-lasting smiles fast, accurate, and a smooth experience.
          </p>
          {/* <Link
            to="/contact"
            className="inline-flex items-center bg-white text-black px-8 py-4 rounded-lg font-medium transition-all duration-200 group"
          >
            Send Case
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link> */}
        </div>
      </section>

      {/* Process Features */}
      <section className="py-20 bg-black "   >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {processFeatures.map((feature, index) => (
              <div
                key={index}
                className="text-center bg-gradient-to-br from-slate-50 to-blue-50 p-8 rounded-xl border border-blue-100"
              >
                <div className="bg-white p-4 rounded-full inline-block mb-6 shadow-lg">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4 Workflow Stages (no big images) */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              Workflow Stages
            </h2>
            <p className="text-lg text-white max-w-3xl mx-auto">
              A clear, step-by-step process from pre-op records to final
              prosthesis delivery.
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2 xl:grid-cols-4">
            {stages.map((stage) => (
              <div
                key={stage.id}
                className={`${stage.bg} rounded-2xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-200 flex flex-col`}
              >
                {/* Top band similar to your sketch */}
                <div className="px-6 pt-6 pb-4 border-b border-gray-100 text-center">
                  <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                    {stage.icon}
                  </div>
                  <p className="text-xs font-semibold tracking-wide text-gray-500 uppercase mb-1">
                    {stage.id}
                  </p>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {stage.title}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {stage.subtitle}
                  </p>
                </div>

                {/* Description under card like handwriting notes */}
                <div className="px-6 py-5 flex-1 flex items-center">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {stage.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {/* <section className="py-20 bg-gray-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-black mb-6">
            We combine innovation, precision, and personalized care
          </h2>
          <h3 className="text-2xl font-semibold text-black-400 mb-8">
            We Are Here to Elevate Your Dental Practice
          </h3>
        </div>
      </section> */}
    </div>
  );
};

export default WorkflowPage;
